/**
 * The contract between the server and the back-office console (WALLIYO-0035).
 *
 * The console is a client like the app: it goes through the API and never
 * touches the database. Every endpoint answers under its own response name
 * (WALLIYO-0068), e.g. `{ "signInStaffResponse": { … } }`.
 *
 * Staff are not customers: they have their own accounts, their own sign-in,
 * and a mandatory second factor (a code sent by SMS). What a staff member may
 * see and do comes only from the feature enablements their roles hold
 * (ADR-0013), resolved by the server on every request (ADR-0014, ADR-0016).
 */

import type { DepartmentId } from "../workspaces/types";
import type { ActionId } from "../actions/definitions";
import type { ActionArea, ActionEffect, ActorType, StaffFeatureId } from "../actions/types";
import type { DefinedPermissionId } from "../permissions/definitions";
import type { MoneyLimit } from "../permissions/types";
import type { Sensitivity } from "../settings/types";

// ── Sign-in: email and password, then a code by SMS ───────────────────────

/** POST /api/v1/console/sign-in */
export type SignInStaffRequest = {
  email: string;
  password: string;
};

/**
 * Sign in with a Google Workspace account (WALLIYO-0120). The token comes from
 * Google Identity Services in the console, issued to the console's own OAuth
 * client. The server accepts it only when the account is managed by the company
 * domain and matches an active staff member — there is no self-service.
 */
export type SignInStaffWithGoogleRequest = {
  /** The Google ID token (a JWT), not an access token. */
  idToken: string;
};

/** Same shape as verifying a code: a session token and when it expires. */
export type SignInStaffWithGoogleResponse = VerifyStaffCodeResponse;

/** A code has been sent by SMS to the staff member's phone. */
export type SignInStaffResponse = {
  challengeId: string;
  /** e.g. "+224 6•• •• •• 41", so they know which phone to check. */
  maskedPhone: string;
  expiresAt: string;
};

/** POST /api/v1/console/verify-code */
export type VerifyStaffCodeRequest = {
  challengeId: string;
  code: string;
};

export type VerifyStaffCodeResponse = {
  /** Sent as `Authorization: Bearer …` on every console request. */
  sessionToken: string;
  /** Sessions end after inactivity (a rule) and can be ended by staff with END_STAFF_SESSIONS. */
  expiresAt: string;
};

/** POST /api/v1/console/sign-out */
export type SignOutStaffResponse = {
  signedOut: true;
};

// ── Who am I, and what may I do ───────────────────────────────────────────

export type StaffRoleSummary = {
  staffRoleId: string;
  name: string;
  /**
   * The department it belongs to, and so the workspace its holders land in
   * (WALLIYO-0132). Null for a role that belongs to no workspace of its own.
   */
  department: DepartmentId | null;
};

export type StaffMemberSummary = {
  staffMemberId: string;
  /**
   * Their UUID: the number on their badge, the one they read out. The console
   * shows it on their own profile — a staff member is asked for it, and the
   * directory is searched by it (WALLIYO-0137). `staffMemberId` is the
   * database's key and is never shown.
   */
  uuid: number;
  name: string;
  email: string;
  roles: readonly StaffRoleSummary[];
};

/**
 * GET /api/v1/console/me
 *
 * Loaded when the console opens and whenever it regains focus. The console
 * shows and hides screens and buttons from `features` and `actions`; the
 * server checks both again on every request, so hiding is only a convenience.
 */
export type GetStaffMeResponse = {
  staffMember: StaffMemberSummary;
  /** Every staff feature enablement that resolves on for this person. */
  features: readonly StaffFeatureId[];
  /**
   * The permissions they hold on top of those features (ADR-0019), each with
   * the most they may do alone, one per currency. Empty: no ceiling.
   */
  permissions: readonly { settingId: DefinedPermissionId; limits: readonly MoneyLimit[] }[];
  /**
   * Every active staff action the console offers them: its feature is on,
   * and no permission they lack refuses it outright. An action whose
   * permission can be asked for is offered: their attempt becomes an approval
   * request.
   */
  actions: readonly ActionId[];
  /** Their own console preferences. */
  settings: {
    consoleTheme: "system" | "light" | "dark";
    consoleLanguage: "fr" | "en" | "de" | "nl" | "pt" | "es" | "it" | "ar";
  };
};

// ── The audit log (ADR-0015) ──────────────────────────────────────────────

/** Entries per page when `limit` is left out. */
export const AUDIT_LOG_DEFAULT_LIMIT = 50;
/** The most entries one page may hold. */
export const AUDIT_LOG_MAX_LIMIT = 200;

/**
 * Failed attempts are logged, not audited. An entry succeeded, was denied, or
 * was `requested`: the staff member may not do it alone, so it became an
 * approval request (ADR-0019 §5).
 */
export type AuditOutcome = "succeeded" | "denied" | "requested";

/** A change, or a read of something. Reading the log is itself audited. */
export type AuditEffect = "read" | "write";

/**
 * GET /api/v1/console/audit-log
 *
 * Every filter is optional and they combine with AND. Sent as query-string
 * parameters. Needs VIEW_AUDIT_LOG_STAFF_FEATURE_ENABLEMENT; without it the
 * request is refused (`feature_not_enabled`) and the refusal is itself audited.
 *
 * Reading is audited (VIEW_AUDIT_LOG_STAFF_ACTION, a sensitive read): who
 * looked, when, and with which filters.
 */
/**
 * The audit's tabs: the same nouns as the nav, so you filter the log with the
 * words you configure with.
 *
 * `actions` is everything that is not one of the other three — an action is
 * not a setting, it is what the settings attach to. The mapping is written
 * out rather than derived, because `userSettings` covers two setting kinds
 * since Staff Settings was folded into it, so the tab names and the kind
 * names stopped being the same list.
 */
export const AUDIT_KIND_ACTIONS = {
  featureEnablements: ["SET_USER_FEATURE_ENABLEMENT_STAFF_ACTION", "SET_STAFF_FEATURE_ENABLEMENT_STAFF_ACTION"],
  // Asking and answering sit with permissions, because that is what moved: a
  // request is how a grant was arrived at, not a different kind of thing.
  permissions: [
    "SET_STAFF_PERMISSION_STAFF_ACTION",
    "REQUEST_PERMISSION_STAFF_ACTION",
    "DECIDE_PERMISSION_REQUEST_STAFF_ACTION",
  ],
  userSettings: ["EDIT_CUSTOMER_SETTING_STAFF_ACTION", "UPDATE_STAFF_SETTING_STAFF_ACTION"],
  // Who works where, and what each role is for (WALLIYO-0131).
  teams: [
    "CREATE_TEAM_STAFF_ACTION",
    "EDIT_TEAM_STAFF_ACTION",
    "SET_TEAM_MEMBERSHIP_STAFF_ACTION",
    "CREATE_ROLE_STAFF_ACTION",
    "EDIT_ROLE_STAFF_ACTION",
    "ASSIGN_ROLE_STAFF_ACTION",
    "REMOVE_ROLE_STAFF_ACTION",
  ],
} as const satisfies Record<string, readonly string[]>;

export type AuditKind = keyof typeof AUDIT_KIND_ACTIONS | "actions";

export const AUDIT_KINDS: readonly AuditKind[] = [
  "featureEnablements",
  "permissions",
  "teams",
  "actions",
  "userSettings",
];

export type ListAuditLogRequest = {
  /** Which tab: the configuration kinds, or everything else. */
  kind?: AuditKind;
  /**
   * Who made the change, by UUID. Separate from `userUuid` on purpose: "what
   * did this person do" and "what was done to this person" are two questions,
   * and one box asking both could answer neither precisely.
   */
  updaterUuid?: number;
  /** Who it was about, by UUID. */
  userUuid?: number;
  /** The person affected: "everything that happened on this account". */
  subjectUserId?: string;
  /** The staff member who acted: "what did this person do". */
  staffMemberId?: string;
  actionId?: ActionId;
  area?: ActionArea;
  outcome?: AuditOutcome;
  /**
   * Changes or reads. Reading the log is itself audited, so a busy console
   * writes far more reads than changes and they bury everything else; the
   * screen asks for `write` unless somebody wants the reads too.
   */
  effect?: AuditEffect;
  /** ISO 8601 date or date-time. Entries at or after it. */
  from?: string;
  /**
   * ISO 8601 date or date-time. Entries strictly before it; a date alone
   * includes that whole day (UTC).
   */
  to?: string;
  /** `nextCursor` from the previous page. Leave out for the first page. */
  cursor?: string;
  /** 1 to AUDIT_LOG_MAX_LIMIT. Defaults to AUDIT_LOG_DEFAULT_LIMIT. */
  limit?: number;
};

export type AuditLogEntry = {
  auditLogId: string;
  occurredAt: string;
  actionId: ActionId;
  /** From the action: a `write` changed something, a `read` only looked. */
  effect: ActionEffect;
  outcome: AuditOutcome;
  actorType: ActorType;
  /** The customer who acted, when `actorType` is `customer`. */
  userId: string | null;
  /** The staff member who acted, when `actorType` is `staff`. */
  staffMemberId: string | null;
  staffSessionId: string | null;
  /** The customer staff acted for, when they acted on someone's behalf. */
  onBehalfOfUserId: string | null;
  /**
   * The actor's UUID — the number a person reads — as recorded at the time.
   * Null on an entry written before UUIDs existed (migration 031): the log is
   * append-only, so those rows were never given one.
   */
  actorUuid: number | null;
  /** The actor's name at that moment. */
  actorLabel: string | null;
  /** Staff only: the roles they held at that moment. */
  actorStaffRoles: readonly string[] | null;
  subjectUserId: string | null;
  /** The subject's UUID, recorded at the time. Null when nobody was acted upon. */
  subjectUuid: number | null;
  /**
   * The subject's name now, looked up when the page is read (it is not stored
   * on the entry). `null` when there is no subject or the account is deleted.
   */
  subjectLabel: string | null;
  area: ActionArea;
  target: { type: string; id: string | null } | null;
  /**
   * Which scope the value was written at — `allClients`, `user`, `staffRole`
   * — for an entry that changed a setting value. Read off the snapshot by the
   * server rather than left in `beforeValue` for the screen to dig out of an
   * `unknown`.
   */
  scope: string | null;
  reason: string | null;
  source: "app" | "console" | "job" | "webhook";
  requestId: string | null;
  sensitive: boolean;
  /**
   * What it was before and after. `null` both when there was nothing to record
   * and when `valuesHidden` is true.
   */
  beforeValue: unknown;
  afterValue: unknown;
  /**
   * True when the entry is sensitive and the reader doesn't hold
   * VIEW_SENSITIVE_AUDIT_VALUES_STAFF_FEATURE_ENABLEMENT. There is no separate
   * call to reveal them: a reader who holds the feature gets them in this same
   * response, and one who doesn't never does.
   */
  valuesHidden: boolean;
};

/** Newest first: by `occurredAt`, then `auditLogId`, both descending. */
export type ListAuditLogResponse = {
  entries: readonly AuditLogEntry[];
  /** Pass as `cursor` for the next page. `null` on the last page. */
  nextCursor: string | null;
};

// ── Rôles & équipe: staff members ─────────────────────────────────────────
//
// Every write takes a `reason` (it goes on the audit entry) and an
// `Idempotency-Key` header, generated once per click (ADR-0016 §2.1): a repeat
// with the same key returns the first answer and changes nothing.
//
// Who may do what (ADR-0013 §2.2), checked by the server on every request:
// - a feature's `sensitivity` decides who may grant or revoke it:
//   - `low`: anyone holding MANAGE_ROLES or ASSIGN_ROLES;
//   - `high`: MANAGE_ROLES, and they must hold that feature themselves;
//   - `critical`: an Owner only, meaning MANAGE_ROLES plus every critical
//     feature. The Owners are told (for now, a server log event);
// - nobody grants or revokes a feature on a role they hold, and nobody
//   assigns or removes their own roles;
// - assigning or removing a role needs the right to grant every feature it
//   holds: you can only give, or take away, what you could grant.
// Refusals answer `feature_not_enabled` with a message saying which rule
// refused, and are audited as denied.

/** The features that open the Rôles & équipe screen: any one of them is enough. */
export const TEAM_SCREEN_FEATURES = [
  "INVITE_STAFF_STAFF_FEATURE_ENABLEMENT",
  "ASSIGN_ROLES_STAFF_FEATURE_ENABLEMENT",
  "MANAGE_ROLES_STAFF_FEATURE_ENABLEMENT",
  "DEACTIVATE_STAFF_STAFF_FEATURE_ENABLEMENT",
] as const satisfies readonly StaffFeatureId[];

/**
 * - `invited`: has not set a password yet, so cannot sign in;
 * - `active`: can sign in;
 * - `deactivated`: can never sign in again. Staff are never deleted.
 */
export type StaffMemberStatus = "invited" | "active" | "deactivated";

/** A team somebody is on, as a list row says it. */
export type StaffTeamSummary = {
  staffTeamId: string;
  name: string;
  leads: boolean;
};

export type StaffMemberListItem = {
  staffMemberId: string;
  /**
   * The number on their badge, which never changes. Shown on every staff
   * screen: a customer has no reason to know theirs, a staff member does.
   */
  uuid: number;
  name: string;
  email: string;
  /** e.g. "+224 6•• •• •• 41". Null when they have none: signing in stopped
   *  needing a phone with Google Workspace (WALLIYO-0120). */
  maskedPhone: string | null;
  status: StaffMemberStatus;
  roles: readonly StaffRoleSummary[];
  /** The open teams they are on, and whether they lead each (WALLIYO-0131). */
  teams: readonly StaffTeamSummary[];
  /**
   * How many things were granted to them by name with no end date.
   *
   * Not what they hold: a grant with an expiry takes itself away, and one
   * without never does — so this is the count somebody has to remember, and
   * the reason a row says an access is due a review (WALLIYO-0131).
   */
  openGrants: number;
  /** Their most recent console request. Null if they never signed in. */
  lastSeenAt: string | null;
  /** How many console sessions are open now. */
  openSessions: number;
  createdAt: string;
  deactivatedAt: string | null;
  version: number;
};

/**
 * GET /api/v1/console/staff
 *
 * Needs any one of TEAM_SCREEN_FEATURES. A plain read: not audited.
 * Sorted: active and invited first, then by name.
 */
export type ListStaffResponse = {
  staffMembers: readonly StaffMemberListItem[];
};

/**
 * POST /api/v1/console/staff (INVITE_STAFF)
 *
 * Creates the staff member with no password and sends them, by SMS, a
 * one-time code valid SET_PASSWORD_CODE_TTL_HOURS. They choose their own
 * password with it (POST /api/v1/console/set-password), then sign in as
 * usual. The code is never returned here: only their phone sees it.
 * An email already used by a staff member is refused (409).
 */
export type InviteStaffRequest = {
  name: string;
  email: string;
  /** E.164, e.g. +224621234567. Where the invite code and every sign-in code go. */
  phone: string;
  reason: string;
  /**
   * The roles they start with, and through them everything they may do
   * (WALLIYO-0120). Optional and may be empty, but someone invited with none
   * signs in to a console with no screens open to them, so the dialog offers
   * the list rather than leaving it to a second step someone forgets.
   *
   * Each one is checked the way assigning it separately would be: you cannot
   * invite someone straight into a role you could not have given them.
   */
  roleIds?: string[];
};

export type InviteStaffResponse = {
  staffMember: StaffMemberListItem;
  /** When the invite code stops working. */
  setPasswordCodeExpiresAt: string;
};

/** How long an invite code works. */
export const SET_PASSWORD_CODE_TTL_HOURS = 48;

/**
 * POST /api/v1/console/staff/:staffMemberId/deactivate (DEACTIVATE_STAFF)
 *
 * Ends all their sessions and their unused invite code in the same
 * transaction. Refused for yourself, and for someone who holds a feature you
 * don't (nobody acts upward).
 */
export type DeactivateStaffRequest = { reason: string };

export type DeactivateStaffResponse = {
  staffMember: StaffMemberListItem;
  endedSessions: number;
};

/**
 * POST /api/v1/console/staff/:staffMemberId/reactivate (DEACTIVATE_STAFF)
 *
 * The undo for deactivating (WALLIYO-0121). Same feature — whoever may take
 * access away may give it back — and the same upward check.
 *
 * Restores nothing but their standing: no session returns and no invite code is
 * reissued, so they sign in again from scratch. Their roles were never removed.
 */
export type ReactivateStaffRequest = { reason: string };

export type ReactivateStaffResponse = {
  staffMember: StaffMemberListItem;
};

/**
 * POST /api/v1/console/staff/:staffMemberId/sessions/end (END_STAFF_SESSIONS)
 *
 * Signs them out everywhere. Refused for someone who holds a feature you
 * don't. Ending your own is allowed (it signs you out too).
 */
export type EndStaffSessionsRequest = { reason: string };

export type EndStaffSessionsResponse = {
  endedSessions: number;
};

/**
 * POST /api/v1/console/set-password
 *
 * Public: there is no session yet. Rate-limited per IP like sign-in, and each
 * code allows SET_PASSWORD_MAX_ATTEMPTS wrong tries before it is spent.
 * The code works once. Password rules: PASSWORD_MIN_LENGTH to
 * PASSWORD_MAX_LENGTH characters, with at least one letter and one character
 * that is not a letter, and not containing the email.
 */
export type SetStaffPasswordRequest = {
  email: string;
  /** The 6 digits sent by SMS with the invite. */
  code: string;
  newPassword: string;
};

export type SetStaffPasswordResponse = {
  passwordSet: true;
};

export const SET_PASSWORD_MAX_ATTEMPTS = 5;
export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 200;

// ── Rôles & équipe: roles ─────────────────────────────────────────────────

export type RoleFeatureGrant = {
  settingId: StaffFeatureId;
  sensitivity: Sensitivity;
  /** Why it was granted, as given when it was. */
  reason: string | null;
  grantedAt: string;
};

export type { MoneyLimit };

/**
 * A permission a role grants (ADR-0019): whether its holders may take the
 * actions it covers, on top of the feature it sits on, and the most they may
 * do alone.
 */
export type RolePermissionGrant = {
  settingId: DefinedPermissionId;
  /** The feature it sits on. The grant counts only while the role's holder has this feature on. */
  feature: StaffFeatureId;
  sensitivity: Sensitivity;
  /** Whether this permission can carry limits at all. */
  limited: boolean;
  /**
   * The most its holders may do alone, one per currency. Empty: no ceiling.
   * An amount in a currency with no limit here needs approval.
   */
  limits: readonly MoneyLimit[];
  reason: string | null;
  grantedAt: string;
};

export type StaffRoleListItem = {
  staffRoleId: string;
  name: string;
  description: string | null;
  /**
   * The department this role belongs to, and so the workspace its holders land
   * in (WALLIYO-0132). Null for a role that belongs to no workspace of its
   * own. Never an access scope: every screen still checks its own feature.
   */
  department: DepartmentId | null;
  features: readonly RoleFeatureGrant[];
  /** The permissions it grants on top of those features (ADR-0019). */
  permissions: readonly RolePermissionGrant[];
  memberCount: number;
  createdAt: string;
  /** Send it back with every change to this role: a stale one is refused. */
  version: number;
};

/**
 * GET /api/v1/console/roles
 *
 * Needs any one of TEAM_SCREEN_FEATURES. A plain read: not audited.
 * Sorted by name.
 */
export type ListRolesResponse = {
  roles: readonly StaffRoleListItem[];
};

/** POST /api/v1/console/roles (MANAGE_ROLES). A name already used is refused (409). */
export type CreateRoleRequest = {
  name: string;
  description?: string | null;
  /**
   * The department it belongs to, and so the workspace its holders land in
   * (WALLIYO-0132).
   *
   * Required. Every role belongs somewhere: an Accountant is Finance, a
   * Verifier is Trust & Safety, and a role created without saying which is a
   * role whose holders land nowhere. Editing can still clear it, because
   * taking a role out of every workspace is a deliberate act.
   */
  department: DepartmentId;
  /**
   * What the role grants from the moment it exists (WALLIYO-0131).
   *
   * A role created with nothing attached has no effect anybody can see, and
   * whoever is creating it is already deciding what it should be able to do.
   * So the grants come with it, in the same transaction: the role and its
   * capabilities appear together or not at all.
   *
   * Every one is authorised on its own, by the same rule as granting it later:
   * nobody grants a feature they do not hold themselves.
   */
  features?: readonly StaffFeatureId[];
  /** The permissions on top of those features (ADR-0019). */
  permissions?: readonly DefinedPermissionId[];
  reason: string;
};

export type CreateRoleResponse = { role: StaffRoleListItem };

/**
 * PATCH /api/v1/console/roles/:staffRoleId (MANAGE_ROLES)
 *
 * `version` is the role's version you were looking at. If it changed since,
 * the edit is refused with `stale_version` (409) and nothing is written:
 * reload the role and try again.
 */
export type EditRoleRequest = {
  name?: string;
  /** `null` clears it. */
  description?: string | null;
  /** `null` takes the role out of every workspace (WALLIYO-0132). */
  department?: DepartmentId | null;
  version: number;
  reason: string;
};

export type EditRoleResponse = { role: StaffRoleListItem };

/**
 * PUT /api/v1/console/roles/:staffRoleId/features/:settingId
 * (SET_STAFF_FEATURE_ENABLEMENT)
 *
 * Grants (`enabled: true`) or revokes (`false`) one staff feature for the
 * role, subject to the sensitivity rules above. `version` is the role's; any
 * change to what the role grants moves it on. Setting what is already true
 * changes nothing and is not an error.
 */
export type SetRoleFeatureRequest = {
  enabled: boolean;
  reason: string;
  version: number;
};

export type SetRoleFeatureResponse = { role: StaffRoleListItem };

/**
 * PUT /api/v1/console/roles/:staffRoleId/permissions/:settingId
 * (SET_STAFF_PERMISSION)
 *
 * Grants (`enabled: true`) or revokes (`false`) one permission for the role
 * (ADR-0019), and sets the most its holders may do alone. The same
 * sensitivity rules as a feature apply, read for the permission: a `high`
 * permission is granted only by someone who holds it themselves, and they
 * can't hand out a higher limit than their own in any currency.
 *
 * - The role must already grant the feature the permission sits on
 *   (`feature_not_granted`, 409). Revoking that feature from the role revokes
 *   the permissions under it.
 * - `limits` is the full set, one per currency. `[]` means no ceiling; left
 *   out, the limits stay as they are. A permission that carries no limit
 *   refuses them (`not_limited`); two in one currency are refused
 *   (`duplicate_currency`).
 * - `version` is the role's. Setting what is already true changes nothing.
 */
export type SetRolePermissionRequest = {
  enabled: boolean;
  limits?: readonly MoneyLimit[];
  reason: string;
  version: number;
};

export type SetRolePermissionResponse = { role: StaffRoleListItem };

/**
 * GET /api/v1/console/permissions/holders?actionId=ISSUE_REFUND_STAFF_ACTION
 *
 * Who can do this (ADR-0019 §8): every active staff member who may take the
 * action now, through which roles, and with what limit. Needs any one of
 * TEAM_SCREEN_FEATURES. A plain read: not audited.
 *
 * `permission` is null when the action's feature alone gates it. Someone who
 * holds the feature but not an approvable permission is listed with
 * `mayRequest: true`: their attempt becomes an approval request.
 */
export type ListPermissionHoldersQuery = { actionId: string };

export type PermissionHolder = {
  staffMemberId: string;
  name: string;
  /** Their roles that grant the permission, or the feature when no permission covers the action. */
  through: readonly { staffRoleId: string; name: string }[];
  /** True when the value is set for them personally, not through a role. */
  personal: boolean;
  /** The most they may do alone, one per currency. Empty: no ceiling. */
  limits: readonly MoneyLimit[];
  /** They may only ask: the action becomes an approval request. */
  mayRequest: boolean;
};

export type ListPermissionHoldersResponse = {
  actionId: string;
  feature: StaffFeatureId | null;
  permission: { settingId: DefinedPermissionId; name: string; approvable: boolean; limited: boolean } | null;
  holders: readonly PermissionHolder[];
};

/**
 * POST   /api/v1/console/staff/:staffMemberId/roles/:staffRoleId (ASSIGN_ROLES)
 * DELETE /api/v1/console/staff/:staffMemberId/roles/:staffRoleId (ASSIGN_ROLES)
 *
 * Assigning what they already hold, or removing what they don't, changes
 * nothing and is not an error.
 */
export type AssignRoleRequest = { reason: string };
export type RemoveRoleRequest = { reason: string };

export type AssignRoleResponse = { staffMember: StaffMemberListItem };
export type RemoveRoleResponse = { staffMember: StaffMemberListItem };

// ── Errors every console endpoint may answer with ─────────────────────────

/**
 * Why a console request was refused, when the server can say more than the
 * code: a stable snake_case identifier a screen can switch on, instead of
 * matching the English message (which may change). These are the ones the
 * screens rely on today; the server may send others, always snake_case and
 * never renamed once sent.
 */
export type ConsoleErrorReason =
  // Fonctionnalités & Règles
  | "critical_requires_owner"
  | "scope_not_supported"
  | "scope_not_allowed"
  | "expiry_in_past"
  | "effective_date_in_past"
  | "legal_basis_required"
  | "invalid_value"
  | "target_not_found"
  | "rule_retired"
  // Dossiers
  | "already_claimed"
  | "case_closed"
  | "not_case_owner"
  // Everywhere
  | "stale_version"
  | "feature_not_enabled"
  // The feature is on for them, but not the permission this action needs (ADR-0019).
  | "permission_not_held"
  | (string & {});

/**
 * Every console failure says what went wrong and carries the request ID that
 * links it to the server log and the audit entry (ADR-0016 §2.5).
 */
export type ConsoleErrorResponse = {
  error: {
    code:
      | "unauthenticated"
      | "session_expired"
      | "feature_not_enabled"
      | "invalid_code"
      | "too_many_attempts"
      | "stale_version"
      | "validation_error"
      | "internal_error";
    /**
     * The specific refusal, when there is one (see ConsoleErrorReason). Absent
     * for a plain validation failure and for anything that failed on the
     * server's side.
     */
    reason?: ConsoleErrorReason;
    message: string;
    requestId: string;
  };
};
